import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import * as authApi from "@/api/auth";
import { event as gaEvent } from "@/lib/gtag";

const KAKAO_CLIENT_ID = import.meta.env.VITE_PUBLIC_KAKAO_JS_KEY;

type View =
  | "initial"
  | "login"
  | "signup-phone"
  | "signup-verify"
  | "signup-form"
  | "terms";

export default function Login() {
  const { isAuthenticated, loginWithKakao, loginWithEmail, signupWithEmail, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // state.from이 있으면 sessionStorage에 저장 (카카오 리다이렉트 시 state 유실 대비)
  const stateFrom = (location.state as { from?: string })?.from;
  if (stateFrom) {
    sessionStorage.setItem("login-redirect", stateFrom);
  }
  const from = stateFrom || sessionStorage.getItem("login-redirect") || "/order";

  const [view, setView] = useState<View>("initial");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // 이메일 폼
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // 휴대폰 인증 (회원가입용)
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [phoneVerifiedToken, setPhoneVerifiedToken] = useState("");

  // 약관 동의 (카카오 신규 유저용)
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const agreeAll = agreeTerms && agreePrivacy;

  // 약관 동의 (이메일 회원가입 인라인용)
  const [signupAgreeTerms, setSignupAgreeTerms] = useState(false);
  const [signupAgreePrivacy, setSignupAgreePrivacy] = useState(false);
  const signupAgreeAll = signupAgreeTerms && signupAgreePrivacy;

  const handleAgreeAll = (checked: boolean) => {
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
  };

  const handleSignupAgreeAll = (checked: boolean) => {
    setSignupAgreeTerms(checked);
    setSignupAgreePrivacy(checked);
  };

  const formatPhone = (value: string) => {
    const nums = value.replace(/\D/g, "").slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 7) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
    return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7)}`;
  };

  const navigateAndCleanup = (path: string) => {
    sessionStorage.removeItem("login-redirect");
    navigate(path, { replace: true });
  };

  // 카카오 리다이렉트 처리
  const code = searchParams.get("code");

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      setError("");
      loginWithKakao(code)
        .then(({ isNewUser }) => {
          gaEvent('login_success', { method: 'kakao', is_new_user: isNewUser });
          if (isNewUser) {
            setView("terms");
          } else {
            navigateAndCleanup(from);
          }
        })
        .catch(() => {
          setError("로그인에 실패했습니다. 다시 시도해주세요.");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [code]);

  useEffect(() => {
    if (isAuthenticated && !code && view !== "terms") {
      navigateAndCleanup(from);
    }
  }, [isAuthenticated, navigate, from, code, view]);

  const handleKakaoLogin = () => {
    gaEvent('login_attempt', { method: 'kakao' });
    const redirectUri = import.meta.env.VITE_KAKAO_REDIRECT_URI;
    const Kakao = (window as any).Kakao;

    // Kakao SDK가 로드되어 있으면 SDK 사용 (앱 설치 시 앱으로 직접 이동)
    if (Kakao) {
      if (!Kakao.isInitialized()) {
        Kakao.init(KAKAO_CLIENT_ID);
      }
      Kakao.Auth.authorize({ redirectUri, scope: "account_email" });
    } else {
      // SDK 미로드 시 기존 방식 fallback
      window.location.href = `https://kauth.kakao.com/oauth/authorize?client_id=${KAKAO_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=account_email`;
    }
  };

  // ─── 이메일 로그인 ───
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    gaEvent('login_attempt', { method: 'email' });
    setIsLoading(true);
    setError("");
    try {
      const { isNewUser } = await loginWithEmail(email, password);
      gaEvent('login_success', { method: 'email', is_new_user: isNewUser });
      if (isNewUser) {
        setView("terms");
      } else {
        navigateAndCleanup(from);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "로그인에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── 회원가입 Step 1: 인증번호 발송 ───
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    setIsLoading(true);
    setError("");
    try {
      const res = await authApi.phoneSend(phone.replace(/-/g, ""));
      setVerificationToken(res.verificationToken);
      setView("signup-verify");
    } catch (err: any) {
      setError(err?.response?.data?.message || "인증번호 발송에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── 회원가입 Step 2: 인증번호 확인 ───
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode) return;
    setIsLoading(true);
    setError("");
    try {
      const res = await authApi.phoneVerify(verificationToken, verificationCode);
      if (res.verified) {
        setPhoneVerifiedToken(res.phoneVerifiedToken);
        setView("signup-form");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "인증번호가 올바르지 않습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── 회원가입 Step 3: 가입 완료 ───
  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    gaEvent('signup_attempt', { method: 'email' });
    if (!email || !password) return;
    if (password !== passwordConfirm) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    if (password.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (!signupAgreeAll) {
      setError("필수 약관에 동의해주세요.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      await signupWithEmail(email, password, phoneVerifiedToken);
      gaEvent('signup_success', { method: 'email' });
      navigateAndCleanup(from);
    } catch (err: any) {
      setError(err?.response?.data?.message || "회원가입에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── 카카오 약관 동의 ───
  const handleRegister = async () => {
    setIsRegistering(true);
    setError("");
    try {
      await register(agreeTerms, agreePrivacy);
      gaEvent('signup_success', { method: 'kakao' });
      navigateAndCleanup(from);
    } catch {
      setError("회원가입에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsRegistering(false);
    }
  };

  // ─── 뷰별 subtitle ───
  const subtitles: Record<View, string> = {
    initial: "AI로 만드는 나만의 웨딩 사진",
    login: "AI로 만드는 나만의 웨딩 사진",
    terms: "서비스 이용을 위해 약관에 동의해주세요",
    "signup-phone": "회원가입을 위해 휴대폰 인증을 진행해주세요",
    "signup-verify": "문자로 발송된 인증번호를 입력해주세요",
    "signup-form": "이메일과 비밀번호를 설정해주세요",
  };

  // ─── 뷰별 콘텐츠 렌더링 ───
  const renderContent = () => {
    // 카카오 약관 동의 화면
    if (view === "terms") {
      return (
        <>
          <h2 className="text-xl font-medium text-gray-800 text-center mb-8">약관 동의</h2>

          <label className="flex items-center gap-3 p-4 bg-[#FDF5EA] rounded-xl cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={agreeAll}
              onChange={(e) => handleAgreeAll(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 cursor-pointer accent-[#C9A96E]"
            />
            <span className="text-[#2D2D2D] font-medium">전체 동의</span>
          </label>

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer flex-1">
                <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[#C9A96E]" />
                <span className="text-sm text-gray-700"><span className="text-red-500 mr-1">[필수]</span>서비스 이용약관 동의</span>
              </label>
              <Link to="/terms" target="_blank" className="text-xs text-gray-400 hover:text-[#C9A96E] underline shrink-0">보기</Link>
            </div>
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer flex-1">
                <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[#C9A96E]" />
                <span className="text-sm text-gray-700"><span className="text-red-500 mr-1">[필수]</span>개인정보처리방침 동의</span>
              </label>
              <Link to="/privacy" target="_blank" className="text-xs text-gray-400 hover:text-[#C9A96E] underline shrink-0">보기</Link>
            </div>
          </div>

          <button
            onClick={handleRegister}
            disabled={!agreeAll || isRegistering}
            className={`w-full py-4 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
              agreeAll ? "bg-[#C9A96E] text-white hover:bg-[#B8956A] shadow-md hover:shadow-lg cursor-pointer" : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isRegistering ? (
              <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>가입 중...</span>
            ) : "동의하고 시작하기"}
          </button>
        </>
      );
    }

    // 회원가입 Step 1: 휴대폰 번호 입력
    if (view === "signup-phone") {
      return (
        <>
          <h2 className="text-xl font-medium text-gray-800 text-center mb-2">휴대폰 인증</h2>
          <p className="text-sm text-gray-500 text-center mb-8">본인 확인을 위해 휴대폰 번호를 입력해주세요</p>

          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">휴대폰 번호</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="010-0000-0000"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#2D2D2D] text-white font-medium rounded-xl hover:bg-[#1a1a1a] transition-all duration-300 cursor-pointer disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>발송 중...</span>
              ) : "인증번호 받기"}
            </button>
          </form>

          <div className="flex items-center justify-center gap-4 mt-5">
            <button onClick={() => { setView("initial"); setError(""); }} className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer">
              뒤로가기
            </button>
          </div>
        </>
      );
    }

    // 회원가입 Step 2: 인증번호 확인
    if (view === "signup-verify") {
      return (
        <>
          <h2 className="text-xl font-medium text-gray-800 text-center mb-2">인증번호 확인</h2>
          <p className="text-sm text-gray-500 text-center mb-8">{phone}으로 발송된 인증번호를 입력해주세요</p>

          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="인증번호 입력"
                required
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-center tracking-[0.5em] font-medium focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#2D2D2D] text-white font-medium rounded-xl hover:bg-[#1a1a1a] transition-all duration-300 cursor-pointer disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>확인 중...</span>
              ) : "확인"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => { setView("signup-phone"); setVerificationCode(""); setError(""); }}
            className="w-full py-2 mt-3 text-sm text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer"
          >
            인증번호 다시 받기
          </button>
        </>
      );
    }

    // 회원가입 Step 3: 이메일/비밀번호 + 약관 동의
    if (view === "signup-form") {
      return (
        <>
          <h2 className="text-xl font-medium text-gray-800 text-center mb-8">회원가입</h2>

          <form onSubmit={handleEmailSignup} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일 주소"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
              />
            </div>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호 (8자 이상)"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>

            <div>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호 확인"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
              />
            </div>

            {/* 약관 동의 */}
            <div className="pt-2 space-y-2.5">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={signupAgreeAll}
                  onChange={(e) => handleSignupAgreeAll(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-[#C9A96E]"
                />
                <span className="text-sm text-[#2D2D2D] font-medium">전체 동의</span>
              </label>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={signupAgreeTerms} onChange={(e) => setSignupAgreeTerms(e.target.checked)} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer accent-[#C9A96E]" />
                  <span className="text-xs text-gray-500"><span className="text-red-500">[필수]</span> 이용약관 동의</span>
                </label>
                <Link to="/terms" target="_blank" className="text-xs text-gray-400 hover:text-[#C9A96E] underline">보기</Link>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={signupAgreePrivacy} onChange={(e) => setSignupAgreePrivacy(e.target.checked)} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer accent-[#C9A96E]" />
                  <span className="text-xs text-gray-500"><span className="text-red-500">[필수]</span> 개인정보처리방침 동의</span>
                </label>
                <Link to="/privacy" target="_blank" className="text-xs text-gray-400 hover:text-[#C9A96E] underline">보기</Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !signupAgreeAll}
              className={`w-full py-3.5 font-medium rounded-xl transition-all duration-300 whitespace-nowrap disabled:opacity-50 ${
                signupAgreeAll ? "bg-[#2D2D2D] text-white hover:bg-[#1a1a1a] cursor-pointer" : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>가입 중...</span>
              ) : "회원가입"}
            </button>
          </form>

          <div className="flex items-center justify-center gap-4 mt-5">
            <button onClick={() => { setView("initial"); setError(""); }} className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer">
              로그인으로 돌아가기
            </button>
          </div>
        </>
      );
    }

    // 초기 / 이메일 로그인 화면
    const showLoginForm = view === "login";

    return isLoading ? (
      <div className="flex flex-col items-center gap-3 py-4">
        <i className="ri-loader-4-line animate-spin text-2xl text-[#C9A96E]"></i>
        <p className="text-sm text-gray-500">{code ? "카카오 로그인 처리 중..." : "처리 중..."}</p>
      </div>
    ) : (
      <>
        {/* 카카오 버튼 */}
        <button
          onClick={handleKakaoLogin}
          className="w-full bg-[#FEE500] hover:bg-[#FDDC00] text-[#3C1E1E] font-medium py-3.5 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
        >
          <i className="ri-kakao-talk-fill text-xl"></i>
          <span className="text-sm">카카오로 시작하기</span>
        </button>

        {/* 구분선 */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-xs text-gray-400">또는</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* 초기: 이메일로 시작하기 버튼 */}
        {!showLoginForm && (
          <>
            <button
              onClick={() => { setView("login"); setError(""); }}
              className="w-full py-3.5 border border-gray-300 rounded-xl flex items-center justify-center gap-3 text-[#2D2D2D] font-medium hover:bg-gray-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-lg"></i>
              <span className="text-sm">이메일로 시작하기</span>
            </button>

            <div className="flex items-center justify-center gap-4 mt-5">
              <Link to="/forgot-password" className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors">비밀번호 찾기</Link>
              <span className="text-gray-300">|</span>
              <button onClick={() => { setView("signup-phone"); setError(""); }} className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer">회원가입</button>
            </div>
          </>
        )}

        {/* 이메일 로그인 폼 */}
        {showLoginForm && (
          <>
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors"
                />
              </div>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A96E] focus:ring-1 focus:ring-[#C9A96E] transition-colors pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-[#2D2D2D] text-white font-medium rounded-xl hover:bg-[#1a1a1a] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                로그인
              </button>
            </form>

            <div className="flex items-center justify-center gap-4 mt-5">
              <Link to="/forgot-password" className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors">비밀번호 찾기</Link>
              <span className="text-gray-300">|</span>
              <button onClick={() => { setView("signup-phone"); setError(""); }} className="text-xs text-gray-400 hover:text-[#C9A96E] transition-colors cursor-pointer">회원가입</button>
            </div>
          </>
        )}
      </>
    );
  };

  // ─── 공통 레이아웃 (단일 렌더 트리) ───
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F0] via-white to-[#FFF0E6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="font-serif text-4xl font-medium tracking-tight text-[#2D2D2D]">봄날</span>
            <span className="text-sm tracking-[0.2em] font-light text-[#C9A96E]">AI WEDDING</span>
          </div>
          <p className="text-gray-600 text-sm font-light mt-4">{subtitles[view]}</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-10">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}
          {renderContent()}
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-[#C9A96E] transition-colors duration-300 inline-flex items-center gap-2">
            <i className="ri-arrow-left-line"></i>
            <span>홈으로 돌아가기</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
